import { selectFavouritesSummary, selectIsFavourite } from '@noths/polaris-client-user-favourites';
import { DeepPartial } from '@noths/polaris-dev-ts-recipes';
import { DecoratedProductForDisplay } from '@noths/polaris-dev-ts-types';
import { createSelector, ParametricSelector } from '@reduxjs/toolkit';

import { ProductPageContainerProps } from 'src/components/organisms/ProductPage';
import { selectPageType } from 'src/redux/navigation/selectors';
import { selectPartnerProductsUrl } from 'src/redux/products/selectors';
import { PageType, UpdatedReduxState } from 'src/types';
import { ReduxApplicationState } from './combinedReducer';

const PRODUCT_TILE_PLACEMENT_QUERY_TYPES = ['I', 'L'];

export const selectCategoryRelatedLinks = (state: ReduxApplicationState) =>
  state.products.category?.seoCategories ?? [];

export const selectCanonicalTag = (state: ReduxApplicationState) => {
  switch (selectPageType(state)) {
    case PageType.Category:
      return state.products.category!.canonicalTag;
    case PageType.Partner:
      return `${selectPartnerProductsUrl(state)}${
        selectCurrentPage(state) > 1 ? `?page=${selectCurrentPage(state)}` : ''
      }`;
    default:
      return '';
  }
};

export const selectLastRequestedPageNumber = (state: ReduxApplicationState) =>
  state.products.lastRequestedPageNumber;

export const selectCurrentPage = (state: ReduxApplicationState) => state.products.currentPage;

export const selectTotalPages = (state: ReduxApplicationState) => state.products.totalPages;

export const selectTotalProducts = (state: ReduxApplicationState) => state.products.totalProducts;

export const selectIsPageWithNavigationTile = (state: ReduxApplicationState) =>
  PRODUCT_TILE_PLACEMENT_QUERY_TYPES.includes(state.products.queryType);

export const selectProductsReturnedCount = (state: ReduxApplicationState) =>
  state.products.productsReturnedCount;

export const selectPaginationTagValues = createSelector(
  [selectCanonicalTag, selectCurrentPage, selectTotalPages],
  (canonicalTag, currentPage, totalPages) => {
    const currentUrl = canonicalTag.split('?')[0];
    const prevPageParam = currentPage === 2 ? '' : `?page=${currentPage - 1}`;
    const nextPageParam = `?page=${currentPage + 1}`;

    const prev = currentPage > 1 ? `${currentUrl}${prevPageParam}` : undefined;
    const next = currentPage < totalPages ? `${currentUrl}${nextPageParam}` : undefined;

    return [prev, next];
  },
);

export const productForPageSelector = (
  state: ReduxApplicationState,
  props: ProductPageContainerProps,
) => state.products.items[props.page];

export const selectProductsForPage: ParametricSelector<
  ReduxApplicationState,
  ProductPageContainerProps,
  DecoratedProductForDisplay[]
> = createSelector(selectFavouritesSummary, productForPageSelector, (favouritesSummary, products) =>
  products.map((product) => {
    const isFavourited = selectIsFavourite(favouritesSummary, product.code);

    return {
      ...product,
      isFavourited,
    };
  }),
);

export const selectLoadingStateForPage: ParametricSelector<
  ReduxApplicationState,
  ProductPageContainerProps,
  boolean
> = createSelector([productForPageSelector], (products) =>
  products.every(({ isLoading }) => isLoading),
);

interface PagePropsForClientHydration {
  updatedReduxState: DeepPartial<UpdatedReduxState>;
}

export const selectPagePropsForClientHydration = ({
  products,
  filter,
  filterMenu,
  userConfiguration,
  navigation,
}: ReduxApplicationState): PagePropsForClientHydration => ({
  updatedReduxState: {
    products,
    userConfiguration: userConfiguration as DeepPartial<typeof userConfiguration>,
    filter: {
      collections: filter.collections,
    },
    filterMenu: {
      sortingOptions: filterMenu.sortingOptions,
      selectedSortOption: filterMenu.selectedSortOption,
    },
    navigation,
  },
});
